import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/useAuth";
import { Header } from "@/components/layout/Header";

export default function Home() {
  const { t } = useTranslation();
  const { isAuthenticated } = useAuth();

  return (
    <div className="min-h-dvh bg-gradient-to-br from-kitchen-warm to-white">
      <Header />

      {/* Hero Section */}
      <section className="container mx-auto px-4 pb-20 pt-32 text-center">
        <h1 className="mb-6 text-5xl font-bold text-gray-900">
          {t("home.hero.title")}
        </h1>
        <p className="mx-auto mb-10 max-w-2xl text-xl text-gray-600">
          {t("home.hero.subtitle")}
        </p>
        <Link
          to={isAuthenticated ? "/dashboard" : "/login"}
          className="inline-block rounded-full bg-primary-600 px-8 py-4 text-lg font-semibold text-white transition-colors hover:bg-primary-700"
        >
          {t("home.hero.cta")}
        </Link>
      </section>

      {/* Features Section */}
      <section className="bg-white py-20">
        <div className="container mx-auto px-4">
          <h2 className="mb-12 text-center text-3xl font-bold text-gray-900">
            {t("home.features.title")}
          </h2>

          <div className="grid gap-8 md:grid-cols-3">
            {/* Feature 1: Catalog */}
            <div className="rounded-xl bg-gray-50 p-8 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
                <svg
                  className="h-8 w-8 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-semibold">
                {t("home.features.catalog.title")}
              </h3>
              <p className="text-gray-600">
                {t("home.features.catalog.description")}
              </p>
            </div>

            {/* Feature 2: Planning */}
            <div className="rounded-xl bg-gray-50 p-8 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
                <svg
                  className="h-8 w-8 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-semibold">
                {t("home.features.planning.title")}
              </h3>
              <p className="text-gray-600">
                {t("home.features.planning.description")}
              </p>
            </div>

            {/* Feature 3: Visualization */}
            <div className="rounded-xl bg-gray-50 p-8 text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-primary-100">
                <svg
                  className="h-8 w-8 text-primary-600"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                  />
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"
                  />
                </svg>
              </div>
              <h3 className="mb-2 text-xl font-semibold">
                {t("home.features.visualization.title")}
              </h3>
              <p className="text-gray-600">
                {t("home.features.visualization.description")}
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Vendors Section */}
      <section className="py-20">
        <div className="container mx-auto px-4 text-center">
          <h2 className="mb-8 text-2xl font-bold text-gray-900">
            Trusted by Leading Manufacturers
          </h2>
          <div className="flex flex-wrap items-center justify-center gap-12 opacity-60">
            <div className="text-2xl font-bold text-gray-500">Niga</div>
            <div className="text-2xl font-bold text-gray-500">Teka</div>
            <div className="text-2xl font-bold text-gray-500">Neolith</div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-gray-900 py-8 text-center text-gray-400">
        <p>&copy; 2025 BYK - Build Your Kitchen. All rights reserved.</p>
      </footer>
    </div>
  );
}
